/**
 * Authentication Visibility Check Plugin
 *
 * Provides proactive session validation when the user returns to the tab/window.
 * This prevents users from encountering cascading API failures and error messages
 * after leaving a tab open for an extended period.
 *
 * Features:
 * - Listens for the document visibilitychange event
 * - When tab becomes visible, checks if the session has expired
 * - Attempts to refresh the token proactively
 * - If refresh fails, shows a single user-friendly message and redirects to login
 * - Prevents the user from interacting with expired sessions
 * - Coordinates with middleware/interceptor to avoid duplicate error handling
 * - Uses broadcastLogoutEvent for cross-tab coordination
 *
 * Flow:
 * 1. User leaves tab open for extended period (e.g., several days)
 * 2. Session expires in the background
 * 3. User returns to the tab (visibilitychange event fires)
 * 4. Plugin checks if session is expired
 * 5. If expired, attempts token refresh
 * 6. If refresh fails, broadcasts logout event and redirects to login before user can interact
 *
 * Benefits:
 * - Proactive detection of expired sessions
 * - Single, clear error message instead of cascade of API failures
 * - Better user experience when returning to stale tabs
 * - Prevents wasted API requests with expired tokens
 * - Shared debounce mechanism prevents race conditions with navigation
 * - Cross-tab logout coordination via storage events
 */

import { executeAuthFailureHandler } from '~/utils/auth.session-handler'
import { broadcastLogoutEvent } from '~/utils/auth.utils'

export default defineNuxtPlugin({
  name: 'auth-visibility-check',
  // Run after auth initialization to ensure auth store is ready
  dependsOn: ['auth-init'],

  setup(nuxtApp) {
    // Only run on client side
    if (!import.meta.client) {
      return
    }

    // Get Pinia instance for proper store access
    const pinia = nuxtApp.$pinia
    const authStore = useAuthStore(pinia)

    /**
     * Handle visibility change event
     * Checks session validity when tab becomes visible
     * 
     * Note: We don't need a local checkInProgress flag because:
     * 1. The auth store's refresh() has built-in single-flight protection
     * 2. The shared executeAuthFailureHandler prevents duplicate error handling
     * 3. If refresh fails, the user is redirected to login anyway
     */
    const handleVisibilityChange = async () => {
      // Only check when tab becomes visible
      if (document.visibilityState !== 'visible') {
        return
      }

      // Skip if not authenticated (nothing to check)
      if (!authStore.isAuthenticated) {
        return
      }

      // Skip if token is still valid (not expired)
      if (!authStore.isTokenExpired) {
        return
      }

      // Token is expired - attempt to refresh proactively
      try {
        // Attempt to refresh the token
        // The auth store's refresh() has single-flight protection
        await authStore.refresh()
        // Refresh succeeded - user can continue
      } catch (error) {
        // Refresh failed - session has truly expired
        // Use shared failure handler to coordinate with middleware/interceptor
        await executeAuthFailureHandler(async () => {
          // Log for debugging
          console.error('Session expired on tab focus - redirecting to login')

          // Use toast to notify user with contextual message
          const toast = useToast()
          toast.add({
            title: 'Session expired',
            description: 'Your session has expired. Please log in again.',
            color: 'amber',
          })

          // Broadcast logout event for cross-tab coordination
          // This ensures other tabs are also notified via storage events
          broadcastLogoutEvent()

          // Clear auth state
          authStore.clearState()

          // Navigate to login
          await navigateTo('/auth/login')
        })
      }
    }

    // Register the visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Cleanup on unmount (when app is destroyed)
    nuxtApp.hook('app:unmounted', () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    })
  },
})
